import CancelOutlined from '@mui/icons-material/CancelOutlined';
import PauseOutlined from '@mui/icons-material/PauseOutlined';
import PowerSettingsNew from '@mui/icons-material/PowerSettingsNew';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { agreementBusinessModelToTranslationMapping } from 'api/responses/models/Unit/ServiceAgreementDto';
import {
	dealerOwnedServiceContractTypeToTranslationMapping,
	volvoOwnedServiceContractTypeToTranslationMapping,
} from 'api/responses/models/Unit/ServiceContractDto';
import { ArrowRight, Trash } from 'assets/volvoIcons';
import { ParseKeys, TOptions } from 'i18next';
import { SectionButton } from 'library/components/controls/buttons/SectionButton';
import {
	InlineDisplayTextContainer,
	InlineDisplayValue,
	InlineDisplayValueUnit,
} from 'library/components/controls/display/InlineDisplayValue';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { Sxable } from 'library/types';

import { DeleteServiceContractDialog } from './DeleteServiceContractDialog';
import { ServiceContractFlag } from './ServiceContractFlag';
import { SuspendReactivateServiceContractDialog } from './SuspendReactivateServiceContractDialog';
import { TerminateServiceContractDialog } from './TerminateServiceContractDialog';
import { ServiceContractDetailsProps } from './types';

const businessModelTranslations: Partial<
	Record<string, ParseKeys<['unitOverview'], TOptions, 'unitOverview'>>
> = agreementBusinessModelToTranslationMapping;

const serviceContractTypeTranslations: Partial<
	Record<
		string,
		ParseKeys<['serviceContracts'], TOptions, 'serviceContracts'>
	>
> = {
	...volvoOwnedServiceContractTypeToTranslationMapping,
	...dealerOwnedServiceContractTypeToTranslationMapping,
};

const actionButtonSx = { minWidth: 'unset', p: 1 };

export const ServiceContractDetails = ({
	serviceContract,
	onBackToList,
	sx,
	withTitle = true,
	withActions = true,
	canManage = false,
	canAddEditDelete = false,
}: ServiceContractDetailsProps & Sxable) => {
	const {
		id,
		status,
		startDate,
		endDate,
		startHour,
		endMaxHour,
		businessModel,
		serviceType,
		responsibleBusinessPartner,
		connectivityIncluded,
		preventiveRepair,
		validatedByVolvoPenta,
	} = serviceContract;
	const { t } = useTranslation([
		'serviceContracts',
		'unitOverview',
		'commonButton',
	]);
	const [isSuspendDialogOpen, setIsSuspendDialogOpen] = useState(false);
	const [isTerminateDialogOpen, setIsTerminateDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const isSuspended = status === 'Paused' || status === 'Suspended';
	const suspendActionLabel = isSuspended
		? t('serviceContracts:reactivate')
		: t('serviceContracts:suspend');

	const businessModelKey = businessModel
		? businessModelTranslations[businessModel]
		: undefined;
	const serviceTypeKey = serviceType
		? serviceContractTypeTranslations[serviceType]
		: undefined;

	return (
		<Paper
			sx={sx}
			id="service-contract-details-container"
			className="service-contract-details"
			variant="bottomBorder">
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 2,
					my: 4,
					mx: 2,
				}}>
				{withTitle && (
					<Typography variant="subtitle2">
						{t('serviceContracts:service-contract-details-title', {
							id,
							status,
						})}
					</Typography>
				)}
				<Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
					{withActions && (
						<>
							{canManage && (
								<>
									<Tooltip title={suspendActionLabel}>
										<Button
											variant="outlined"
											aria-label={suspendActionLabel}
											sx={actionButtonSx}
											onClick={() =>
												setIsSuspendDialogOpen(true)
											}>
											{isSuspended ? (
												<PowerSettingsNew />
											) : (
												<PauseOutlined />
											)}
										</Button>
									</Tooltip>
									<Tooltip
										title={t('serviceContracts:terminate')}>
										<Button
											variant="outlined"
											aria-label={t(
												'serviceContracts:terminate'
											)}
											sx={actionButtonSx}
											onClick={() =>
												setIsTerminateDialogOpen(true)
											}>
											<CancelOutlined />
										</Button>
									</Tooltip>
								</>
							)}
							{canAddEditDelete && (
								<Tooltip title={t('commonButton:delete')}>
									<Button
										variant="outlined"
										aria-label={t('commonButton:delete')}
										sx={actionButtonSx}
										onClick={() =>
											setIsDeleteDialogOpen(true)
										}>
										<Trash />
									</Button>
								</Tooltip>
							)}
						</>
					)}
				</Box>
			</Box>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					gap: 8,
					mx: 2,
					mb: 4,
				}}>
				<InlineDisplayTextContainer
					columnsCount={1}
					rowsCount={7}
					sx={{ flex: 1 }}>
					<InlineDisplayValue
						label={t('serviceContracts:start-date')}
						value={getLocalizedDateFormat(startDate)}
					/>
					<InlineDisplayValue
						label={t('serviceContracts:end-date')}
						value={getLocalizedDateFormat(endDate)}
					/>
					<InlineDisplayValueUnit
						label={t('serviceContracts:start-hour')}
						value={startHour?.toLocaleString()}
						unit={t('serviceContracts:hours-unit')}
					/>
					<InlineDisplayValueUnit
						label={t('serviceContracts:end-max-hour')}
						value={endMaxHour?.toLocaleString()}
						unit={t('serviceContracts:hours-unit')}
					/>
					<InlineDisplayValue
						label={t('serviceContracts:business-model')}
						value={
							businessModelKey
								? t(businessModelKey)
								: businessModel
						}
					/>
					<InlineDisplayValue
						label={t('serviceContracts:service-contract-type')}
						value={serviceTypeKey ? t(serviceTypeKey) : serviceType}
					/>
					<InlineDisplayValue
						label={t(
							'serviceContracts:responsible-business-partner'
						)}
						value={responsibleBusinessPartner}
					/>
				</InlineDisplayTextContainer>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 4,
						flex: 1,
					}}>
					<ServiceContractFlag
						label={t('serviceContracts:connectivity-included')}
						description={t(
							'serviceContracts:connectivity-included-description'
						)}
						value={connectivityIncluded}
					/>
					<ServiceContractFlag
						label={t('serviceContracts:preventive-repair')}
						description={t(
							'serviceContracts:preventive-repair-description'
						)}
						value={preventiveRepair}
					/>
					<ServiceContractFlag
						label={t('serviceContracts:validated-by-volvo-penta')}
						description={t(
							'serviceContracts:validated-by-volvo-penta-description'
						)}
						value={validatedByVolvoPenta}
					/>
				</Box>
			</Box>
			{onBackToList && (
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', m: 2 }}>
					<SectionButton
						text={t('serviceContracts:contracts-list')}
						icon={<ArrowRight />}
						onClick={onBackToList}
					/>
				</Box>
			)}
			{isSuspendDialogOpen && (
				<SuspendReactivateServiceContractDialog
					serviceContract={serviceContract}
					onClose={() => setIsSuspendDialogOpen(false)}
					onConfirm={() => setIsSuspendDialogOpen(false)}
				/>
			)}
			{isTerminateDialogOpen && (
				<TerminateServiceContractDialog
					serviceContract={serviceContract}
					onClose={() => setIsTerminateDialogOpen(false)}
					onConfirm={() => setIsTerminateDialogOpen(false)}
				/>
			)}
			{isDeleteDialogOpen && (
				<DeleteServiceContractDialog
					serviceContract={serviceContract}
					onClose={() => setIsDeleteDialogOpen(false)}
					onConfirm={() => {
						setIsDeleteDialogOpen(false);
						onBackToList?.();
					}}
				/>
			)}
		</Paper>
	);
};
